/**
 * Input layer. Quantizes to the sim's integer domain BEFORE anything is
 * recorded, so keyboard and touch produce the same run for the same
 * intent, and a replay never depends on this file's precision.
 */
import { quantize, MAG_MAX } from '@bonwarsy/run-engine';

export class Input {
  private keys = new Set<string>();
  /**
   * Discrete presses latch here until consumed. Reading them from the
   * held-key set loses any tap whose keydown and keyup both land between
   * two frames — which is most taps, and it makes level-up cards randomly
   * unresponsive.
   */
  private taps = new Set<string>();
  private pointerActive = false;
  private originX = 0;
  private originY = 0;
  private curX = 0;
  private curY = 0;
  /**
   * Where the squad was when this drag began, as a corridor fraction.
   * The squad moves BY the finger's displacement from here, so a drag
   * never teleports the squad to wherever the thumb happened to land.
   * Latched on the first read after pointerdown, because the input layer
   * does not own the sim's position.
   */
  private anchorSquad = 0;
  private needAnchor = false;
  /** Set on any interaction, so audio can be unlocked from a gesture. */
  gestured = false;

  constructor(
    private readonly el: HTMLElement,
    private readonly dirTable: Float64Array,
    /** Finger travel to corridor travel. 1.0 is 1:1 across the screen. */
    private readonly dragGain: number,
    /**
     * Which way world +x points on screen (SCREEN_X_SIGN from the render
     * layer). The chase camera looks along +z, so +x is screen LEFT and a
     * drag must be applied with this sign or the whole control scheme is
     * mirrored.
     */
    private readonly screenXSign: number,
  ) {
    window.addEventListener('keydown', (e) => {
      this.keys.add(e.key.toLowerCase());
      this.taps.add(e.key.toLowerCase());
      this.gestured = true;
      if (['arrowup', 'arrowdown', 'arrowleft', 'arrowright', ' '].includes(e.key.toLowerCase())) e.preventDefault();
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.key.toLowerCase()));

    const down = (x: number, y: number): void => {
      this.pointerActive = true; this.gestured = true;
      this.originX = x; this.originY = y; this.curX = x; this.curY = y;
      this.needAnchor = true;
    };
    const move = (x: number, y: number): void => { this.curX = x; this.curY = y; };
    const up = (): void => { this.pointerActive = false; };

    el.addEventListener('pointerdown', (e) => { el.setPointerCapture(e.pointerId); down(e.clientX, e.clientY); });
    el.addEventListener('pointermove', (e) => { if (this.pointerActive) move(e.clientX, e.clientY); });
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  }

  pressed(key: string): boolean { return this.keys.has(key); }
  consume(key: string): boolean {
    if (!this.taps.has(key)) return false;
    this.taps.delete(key);
    this.keys.delete(key);
    return true;
  }

  /**
   * RELATIVE steering: the squad moves by however far the finger has
   * moved since the touch began, from wherever the squad already was.
   *
   * This replaced absolute steering ("wherever your thumb is across the
   * screen is where the squad goes"). Absolute steering sounds lower
   * latency, but combined with lane quantisation it meant a short drag
   * did nothing at all until the thumb crossed the screen midpoint, and
   * then the squad jumped a whole lane. That is a tap, not a swipe.
   *
   * @param squadFrac where the squad is now, as a corridor fraction.
   * @returns a target in [0,1] across the corridor, or null for "hold".
   */
  targetX(squadFrac: number): number | null {
    if (!this.pointerActive) return null;
    if (this.needAnchor) { this.anchorSquad = squadFrac; this.needAnchor = false; }
    const rect = this.el.getBoundingClientRect();
    const moved = (this.curX - this.originX) / Math.max(1, rect.width);
    const t = this.anchorSquad + moved * this.dragGain * this.screenXSign;
    return t < 0 ? 0 : t > 1 ? 1 : t;
  }

  /** Keyboard fallback, still quantized through the same domain. */
  read(): { dir: number; mag: number } {
    let x = 0;
    let y = 0;
    if (this.keys.has('a') || this.keys.has('arrowleft')) x -= 1;
    if (this.keys.has('d') || this.keys.has('arrowright')) x += 1;
    if (this.keys.has('w') || this.keys.has('arrowup')) y += 1;
    if (this.keys.has('s') || this.keys.has('arrowdown')) y -= 1;
    if (x === 0 && y === 0) return { dir: 0, mag: 0 };
    const q = quantize(this.dirTable, x, y);
    return { dir: q.dir, mag: Math.max(1, Math.min(MAG_MAX, q.mag)) };
  }
}
